import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Table,
  Segmented,
  Skeleton,
  Empty,
  Drawer,
  Tag,
  Tooltip as AntTooltip,
  Button as AntButton,
  Progress,
} from 'antd';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import {
  RefreshCw,
  ArrowLeft,
  Users,
  LogIn,
  Clock,
  Layers,
  AlertTriangle,
  MousePointerClick,
  Mail,
  ChevronRight,
} from 'lucide-react';

import { useApi } from '../../hooks/useApi';
import { useTheme } from '../../contexts/ThemeContext';
import { getAdminDesignSystem, ADMIN_ROOT, AdminThemeProvider, getAdminTokens } from './adminDesignSystem';
import AdminSidebar from './AdminSidebar';

/**
 * ============================================================================
 *  ORG PATTERN — what an organization's people actually do.
 * ----------------------------------------------------------------------------
 *  Two views in one file:
 *    • no :orgId  → cross-org league table, ranked by engagement
 *    • :orgId     → that org's full behavioural pattern
 *
 *  Data comes from a stored snapshot that the server builds ON DEMAND and reuses
 *  for a few hours. There is no scheduled job — nothing is computed unless someone
 *  opens this page, and repeat visits are a single indexed read.
 *
 *  Mongo is the primary source (logins, sessions, rhythm). Page-level panels —
 *  footprint, never-opened, journeys, friction — need external page tracking, and
 *  when that isn't reporting they say "not tracked" rather than showing zero.
 *  Claiming a module is unused when we simply weren't watching would be a lie, and
 *  we never invent numbers to fill a panel.
 * ============================================================================
 */

const VERDICT_META = {
  healthy: { label: 'Healthy', tone: 'ok' },
  slipping: { label: 'Engagement slipping', tone: 'warn' },
  at_risk: { label: 'At risk', tone: 'bad' },
  dormant: { label: 'Dormant', tone: 'bad' },
  no_data: { label: 'No data collected yet', tone: 'off' },
};

const LABEL_TONE = {
  'Power user': 'teal',
  Regular: 'blue',
  Evaluator: 'purple',
  Drifting: 'orange',
  Dormant: 'bad',
  'Never activated': 'off',
};

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
const secsToHuman = (s) => {
  if (!s) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m}m` : `${(m / 60).toFixed(1)}h`;
};
const sinceHuman = (d) => {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const h = diff / 3600000;
  if (h < 1) return 'Just now';
  if (h < 24) return `${Math.round(h)}h ago`;
  const days = Math.round(h / 24);
  return days === 1 ? 'Yesterday' : `${days} days ago`;
};

const OrgPattern = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { isDark } = useTheme();
  const tokens = useMemo(() => getAdminTokens(isDark), [isDark]);
  const designSystem = useMemo(() => getAdminDesignSystem(isDark), [isDark]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [days, setDays] = useState(30);

  const [leagueRows, setLeagueRows] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [org, setOrg] = useState(null);
  const [people, setPeople] = useState([]);
  const [message, setMessage] = useState('');

  const [drawerUser, setDrawerUser] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [delivered, setDelivered] = useState([]);
  const [events, setEvents] = useState([]);

  /* ── data ───────────────────────────────────────────────────────────── */

  const loadLeague = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/v1/admin/org-pattern');
      setLeagueRows(res?.data?.data || []);
    } catch (err) {
      toast.error('Could not load organizations');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadOrg = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const [main, del, ev] = await Promise.all([
        api.get(`/api/v1/admin/org-pattern/${orgId}?days=${days}`),
        api.get(`/api/v1/admin/org-pattern/${orgId}/delivered`),
        api.get(`/api/v1/admin/org-pattern/${orgId}/events?limit=100`),
      ]);
      setSnapshot(main?.data?.data || null);
      setOrg(main?.data?.org || null);
      setPeople(main?.data?.people || []);
      setMessage(main?.data?.message || '');
      setDelivered(del?.data?.data || []);
      setEvents(ev?.data?.data || []);
    } catch (err) {
      toast.error('Could not load org pattern');
    } finally {
      setLoading(false);
    }
  }, [api, orgId, days]);

  useEffect(() => {
    if (orgId) loadOrg();
    else loadLeague();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, days]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/api/v1/admin/org-pattern/refresh', orgId ? { orgId } : {});
      toast.success('Snapshot rebuilt');
      if (orgId) await loadOrg(); else await loadLeague();
    } catch (err) {
      toast.error('Rebuild failed');
    } finally {
      setRefreshing(false);
    }
  };

  const openUser = async (row) => {
    setDrawerUser(row);
    setDrawerLoading(true);
    setDrawerData(null);
    try {
      const res = await api.get(`/api/v1/admin/org-pattern/${orgId}/users/${row.userId}?days=${days}`);
      setDrawerData(res?.data?.data || null);
    } catch (err) {
      toast.error('Could not load this person');
    } finally {
      setDrawerLoading(false);
    }
  };

  /* ── small presentational helpers ────────────────────────────────────── */

  const toneColor = (tone) => ({
    ok: tokens.okTx, warn: tokens.warnTx, bad: tokens.badTx,
    off: tokens.text3, teal: tokens.tealTx, blue: tokens.blueTx,
    purple: tokens.purpleTx, orange: tokens.orangeTx,
  }[tone] || tokens.text2);

  const toneBg = (tone) => ({
    ok: 'rgba(26,163,99,0.11)', warn: 'rgba(224,160,32,0.13)', bad: 'rgba(229,81,75,0.11)',
    off: tokens.panel2, teal: tokens.tealBg, blue: tokens.blueBg,
    purple: tokens.purpleBg, orange: tokens.orangeBg,
  }[tone] || tokens.panel2);

  const Pill = ({ tone, children }) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 12px',
      borderRadius: 999, fontSize: 12.5, fontWeight: 700,
      background: toneBg(tone), color: toneColor(tone),
    }}>
      <i style={{ width: 7, height: 7, borderRadius: 999, background: toneColor(tone) }} />
      {children}
    </span>
  );

  const Stat = ({ label, value, sub, icon: Icon }) => (
    <div style={{ background: tokens.panel, padding: '18px 20px' }}>
      <div style={{
        fontSize: 11.5, fontWeight: 600, color: tokens.text3,
        textTransform: 'uppercase', letterSpacing: '0.55px',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        {Icon ? <Icon size={12} /> : null}{label}
      </div>
      <div style={{
        fontSize: 26, fontWeight: 700, letterSpacing: '-0.9px', marginTop: 6,
        color: tokens.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1.05,
      }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, color: tokens.text2, marginTop: 4 }}>{sub}</div> : null}
    </div>
  );

  const Card = ({ title, hint, children, pad = true }) => (
    <div style={{
      background: tokens.panel, border: `1px solid ${tokens.border}`,
      borderRadius: 20, boxShadow: tokens.sh, overflow: 'hidden',
    }}>
      <div style={{ padding: '18px 22px 0', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: tokens.text }}>{title}</h2>
        {hint ? <span style={{ fontSize: 12.5, color: tokens.text3 }}>{hint}</span> : null}
      </div>
      <div style={{ padding: pad ? '16px 22px 22px' : '14px 6px 6px' }}>{children}</div>
    </div>
  );

  const BarRow = ({ label, value, max, tone = 'teal', width = 160 }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 12 }}>
      <div style={{
        width, flex: 'none', fontFamily: 'ui-monospace, SF Mono, Menlo, monospace',
        fontSize: 12.5, color: tokens.text2, overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{label}</div>
      <div style={{ flex: 1, height: 7, borderRadius: 999, background: tokens.hair, overflow: 'hidden' }}>
        <i style={{
          display: 'block', height: '100%', borderRadius: 999,
          width: `${max ? Math.max(2, (value / max) * 100) : 0}%`,
          background: toneColor(tone),
        }} />
      </div>
      <span style={{
        fontSize: 12, color: tokens.text2, fontVariantNumeric: 'tabular-nums',
        minWidth: 46, textAlign: 'right',
      }}>{fmt(value)}</span>
    </div>
  );

  /* ── heatmap ─────────────────────────────────────────────────────────── */

  const Heatmap = ({ hourHist, dowHist }) => {
    const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const hours = hourHist?.length === 24 ? hourHist : new Array(24).fill(0);
    const dows = dowHist?.length === 7 ? dowHist : new Array(7).fill(0);
    const hMax = Math.max(...hours, 1);
    const dMax = Math.max(...dows, 1);

    // The snapshot stores the two histograms separately (cheap to compute and
    // store); the grid shows their product, which is the shape a reader wants.
    return (
      <div style={{ overflowX: 'auto' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: `42px repeat(24, minmax(14px, 1fr))`,
          gap: 3, minWidth: 680,
        }}>
          <div />
          {hours.map((_, h) => (
            <div key={`h${h}`} style={{
              fontSize: 9.5, color: tokens.text3, textAlign: 'center',
              height: 15, fontVariantNumeric: 'tabular-nums',
            }}>{h % 3 === 0 ? h : ''}</div>
          ))}
          {DAYS.map((d, di) => (
            <React.Fragment key={d}>
              <div style={{
                fontSize: 10.5, color: tokens.text3, display: 'flex',
                alignItems: 'center', height: 22, fontWeight: 600,
              }}>{d}</div>
              {hours.map((hv, h) => {
                const v = (hv / hMax) * (dows[di] / dMax);
                return (
                  <AntTooltip key={`${d}-${h}`} title={`${d} ${h}:00`}>
                    <i style={{
                      height: 22, borderRadius: 4, display: 'block',
                      background: v < 0.06
                        ? tokens.hair
                        : `rgba(0,168,155,${(0.14 + v * 0.86).toFixed(2)})`,
                    }} />
                  </AntTooltip>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  /* ── league table (no orgId) ─────────────────────────────────────────── */

  const leagueColumns = [
    {
      title: 'Organization', dataIndex: 'orgName', key: 'orgName',
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: tokens.text }}>{v || 'Unnamed'}</div>
          <div style={{ fontSize: 11.5, color: tokens.text3 }}>{r.tz}</div>
        </div>
      ),
    },
    {
      title: 'Verdict', dataIndex: 'verdict', key: 'verdict',
      render: (v) => {
        const m = VERDICT_META[v] || VERDICT_META.no_data;
        return <Pill tone={m.tone}>{m.label}</Pill>;
      },
    },
    {
      title: 'Active', key: 'active', align: 'right',
      sorter: (a, b) => a.activeUsers - b.activeUsers,
      render: (_, r) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {fmt(r.activeUsers)}
          <span style={{ color: tokens.text3 }}>/{fmt(r.seats)}</span>
        </span>
      ),
    },
    {
      title: 'Logins', dataIndex: 'logins', key: 'logins', align: 'right',
      sorter: (a, b) => a.logins - b.logins,
      render: (v) => fmt(v),
    },
    {
      title: 'Modules', key: 'modules', align: 'right',
      render: (_, r) => `${r.modulesUsed}/${r.modulesTotal}`,
    },
    {
      title: 'Last seen', dataIndex: 'lastSeenAt', key: 'lastSeenAt',
      render: (v) => <span style={{ color: tokens.text2 }}>{sinceHuman(v)}</span>,
    },
    {
      title: '', key: 'go', width: 40,
      render: () => <ChevronRight size={15} color={tokens.text3} />,
    },
  ];

  /* ── people table ────────────────────────────────────────────────────── */

  const peopleColumns = [
    {
      title: 'User', key: 'user',
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: tokens.text }}>{r.name}</div>
          <div style={{ fontSize: 11.5, color: tokens.text3 }}>{r.email}</div>
        </div>
      ),
    },
    {
      title: 'Pattern', dataIndex: 'label', key: 'label',
      render: (v) => {
        const tone = LABEL_TONE[v] || 'off';
        return (
          <Tag style={{
            background: toneBg(tone), color: toneColor(tone),
            border: tone === 'off' ? `1px solid ${tokens.border2}` : 'none',
            borderRadius: 8, fontWeight: 600, fontSize: 11.5, padding: '3px 9px',
          }}>{v}</Tag>
        );
      },
    },
    {
      title: 'Logins', dataIndex: 'logins', key: 'logins', align: 'right',
      sorter: (a, b) => (a.logins || 0) - (b.logins || 0), defaultSortOrder: 'descend',
      // null means login counting hasn't started yet — showing 0 would read as
      // "never logged in" for someone who is demonstrably active.
      render: (v, r) => (r.countsPending
        ? <AntTooltip title="Login counting starts once activity recording is live"><span style={{ color: tokens.text3 }}>—</span></AntTooltip>
        : fmt(v)),
    },
    {
      title: 'Days active', key: 'daysActive', align: 'right',
      sorter: (a, b) => a.daysActive - b.daysActive,
      render: (_, r) => (r.countsPending
        ? <span style={{ color: tokens.text3 }}>—</span>
        : <span>{r.daysActive}<span style={{ color: tokens.text3 }}>/{days}</span></span>),
    },
    {
      title: 'Last seen', dataIndex: 'lastAt', key: 'lastAt',
      render: (v) => <span style={{ color: tokens.text2 }}>{sinceHuman(v)}</span>,
    },
  ];

  /* ── render: league ──────────────────────────────────────────────────── */

  const renderLeague = () => (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: tokens.text, letterSpacing: '-0.03em', margin: 0 }}>
            Org Patterns
          </h1>
          <p style={{ fontSize: 13, color: tokens.text3, margin: '4px 0 0' }}>
            Who is actually using the product — ranked by engagement. Click an org for its full pattern.
          </p>
        </div>
        <AntButton icon={<RefreshCw size={14} />} loading={refreshing} onClick={refresh}>
          Rebuild snapshots
        </AntButton>
      </div>

      {loading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : leagueRows.length === 0 ? (
        <Card title="No snapshots yet">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: tokens.text2 }}>
                The nightly run hasn't produced any snapshots yet. Use <b>Rebuild snapshots</b> to
                generate them now, or wait for 01:30.
              </span>
            }
          />
        </Card>
      ) : (
        <Card title="All organizations" hint={`${leagueRows.length} with snapshots`} pad={false}>
          <Table
            rowKey={(r) => String(r.orgId)}
            columns={leagueColumns}
            dataSource={leagueRows}
            pagination={{ pageSize: 25, showSizeChanger: false }}
            onRow={(r) => ({
              style: { cursor: 'pointer' },
              onClick: () => navigate(`/main-admin/org-pattern/${r.orgId}`),
            })}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      )}
    </>
  );

  /* ── render: single org ──────────────────────────────────────────────── */

  const renderOrg = () => {
    const v = VERDICT_META[snapshot?.verdict] || VERDICT_META.no_data;
    const topMax = snapshot?.topPages?.[0]?.v || 1;
    const rageMax = snapshot?.rageclicks?.[0]?.n || 1;

    return (
      <>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <AntButton
            size="small"
            icon={<ArrowLeft size={13} />}
            onClick={() => navigate('/main-admin/org-pattern')}
          >
            All orgs
          </AntButton>
          <div style={{ flex: 1 }} />
          <Segmented
            value={days}
            onChange={setDays}
            options={[{ label: '7d', value: 7 }, { label: '30d', value: 30 }, { label: '90d', value: 90 }]}
          />
          <AntButton icon={<RefreshCw size={14} />} loading={refreshing} onClick={refresh}>
            Rebuild
          </AntButton>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: tokens.text, letterSpacing: '-0.03em', margin: 0 }}>
              {org?.name || snapshot?.orgName || 'Organization'}
            </h1>
            <div style={{
              marginTop: 5, fontSize: 13, color: tokens.text2,
              display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap',
            }}>
              <span style={{ fontFamily: 'ui-monospace, SF Mono, Menlo, monospace' }}>{orgId}</span>
              {snapshot?.tz ? <><i style={{ width: 1, height: 12, background: tokens.border2 }} /><span>{snapshot.tz}</span></> : null}
              {snapshot?.day ? <><i style={{ width: 1, height: 12, background: tokens.border2 }} /><span>Snapshot {dayjs(snapshot.day).format('D MMM')}</span></> : null}
            </div>
          </div>
          {snapshot ? <Pill tone={v.tone}>{v.label}</Pill> : null}
        </div>

        {loading ? (
          <Skeleton active paragraph={{ rows: 10 }} />
        ) : !snapshot ? (
          <Card title="No snapshot yet">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: tokens.text2 }}>
                  {message || 'The nightly run has not covered this org yet.'}
                  <br />Use <b>Rebuild</b> to generate it now.
                </span>
              }
            />
          </Card>
        ) : (
          <>
            {/* health strip */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))',
              gap: 1, background: tokens.hair, borderRadius: 20, overflow: 'hidden',
              border: `1px solid ${tokens.border}`, boxShadow: tokens.sh,
            }}>
              <Stat icon={Users} label="Active users"
                value={<>{fmt(snapshot.activeUsers)}<span style={{ fontSize: 15, color: tokens.text3, fontWeight: 600 }}>/{fmt(snapshot.seats)}</span></>}
                sub={`${snapshot.seats ? Math.round((snapshot.activeUsers / snapshot.seats) * 100) : 0}% of seats`} />
              <Stat icon={LogIn} label="Logins" value={fmt(snapshot.logins)}
                sub={snapshot.trend === 'up' ? '↑ trending up' : snapshot.trend === 'down' ? '↓ trending down' : '→ flat'} />
              <Stat icon={Clock} label="Avg session" value={secsToHuman(snapshot.avgSessionSec)}
                sub={`${snapshot.pagesPerSession || 0} pages/session`} />
              <Stat icon={Layers} label="Modules used"
                value={snapshot.pageDataAvailable
                  ? <>{snapshot.modulesUsed}<span style={{ fontSize: 15, color: tokens.text3, fontWeight: 600 }}>/{snapshot.modulesTotal}</span></>
                  : <span style={{ fontSize: 17, color: tokens.text3 }}>Not tracked</span>}
                sub={snapshot.pageDataAvailable ? `${snapshot.neverOpened?.length || 0} never opened` : 'needs page tracking'} />
              <Stat label="Sessions" value={fmt(snapshot.sessions)}
                sub={snapshot.pageDataAvailable ? `${fmt(snapshot.pageviews)} page views` : 'tracked work sessions'} />
              <Stat label="Last seen" value={<span style={{ fontSize: 19 }}>{sinceHuman(snapshot.lastSeenAt)}</span>} />
            </div>

            {/* actions */}
            {snapshot.actions?.length ? (
              <>
                <div style={{
                  fontSize: 11.5, fontWeight: 700, color: tokens.text3,
                  textTransform: 'uppercase', letterSpacing: '0.6px', margin: '28px 0 12px',
                }}>Recommended actions</div>
                {snapshot.actions.map((a, i) => {
                  const tone = a.severity === 'high' ? 'bad' : 'orange';
                  return (
                    <div key={i} style={{
                      background: tokens.panel, border: `1px solid ${tokens.border}`,
                      borderLeft: `3px solid ${toneColor(tone)}`, borderRadius: 16,
                      padding: '15px 18px', display: 'flex', alignItems: 'center',
                      gap: 14, boxShadow: tokens.sh, marginBottom: 10,
                    }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: 11, display: 'grid',
                        placeItems: 'center', flex: 'none',
                        background: toneBg(tone), color: toneColor(tone),
                      }}><AlertTriangle size={16} /></div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13.5, color: tokens.text }}>{a.title}</div>
                        <div style={{ fontSize: 12.5, color: tokens.text2, marginTop: 2 }}>{a.detail}</div>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : null}

            {/* rhythm */}
            <div style={{ marginTop: 22 }}>
              <Card title="Login rhythm" hint={`when this org works · ${snapshot.tz}${snapshot.pageDataAvailable ? '' : ' · from logins'}`}>
                <Heatmap hourHist={snapshot.hourHist} dowHist={snapshot.dowHist} />
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5,
                  color: tokens.text3, marginTop: 13, flexWrap: 'wrap',
                }}>
                  <span>Quiet</span>
                  {[tokens.hair, 'rgba(0,168,155,0.22)', 'rgba(0,168,155,0.45)', 'rgba(0,168,155,0.7)', tokens.accent].map((c, i) => (
                    <i key={i} style={{ width: 15, height: 11, borderRadius: 3, background: c, display: 'block' }} />
                  ))}
                  <span>Peak</span>
                </div>
              </Card>
            </div>

            {/* people */}
            <div style={{ marginTop: 18 }}>
              <Card title="People" hint={`${people.length} with recorded activity · click a row`} pad={false}>
                {people.length === 0 ? (
                  <div style={{ padding: '8px 16px 20px' }}>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={<span style={{ color: tokens.text2 }}>No logins recorded in this window.</span>}
                    />
                  </div>
                ) : (
                  <Table
                    rowKey={(r) => String(r.userId)}
                    columns={peopleColumns}
                    dataSource={people}
                    pagination={people.length > 15 ? { pageSize: 15, showSizeChanger: false } : false}
                    onRow={(r) => ({ style: { cursor: 'pointer' }, onClick: () => openUser(r) })}
                    scroll={{ x: 'max-content' }}
                  />
                )}
              </Card>
            </div>

            {/* footprint + never opened */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 18, marginTop: 18 }}>
              <Card title="Page footprint" hint={snapshot.pageDataAvailable ? 'most → least' : 'not tracked'}>
                {snapshot.topPages?.length ? snapshot.topPages.slice(0, 8).map((p) => (
                  <BarRow key={p.p} label={p.p} value={p.v} max={topMax} />
                )) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<span style={{ color: tokens.text2 }}>
                      Page-level tracking isn't reporting for this org, so which pages
                      they open isn't known. Every other number on this page is unaffected.
                    </span>} />
                )}
              </Card>

              <Card title="Never opened" hint={snapshot.pageDataAvailable ? 'paid for, not used' : 'not tracked'}>
                {!snapshot.pageDataAvailable ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<span style={{ color: tokens.text2 }}>
                      Needs page-level tracking. Without it we can't tell an unused
                      module from one we simply weren't watching.
                    </span>} />
                ) : snapshot.neverOpened?.length ? snapshot.neverOpened.map((p) => (
                  <div key={p} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 12, padding: '11px 0', borderTop: `1px solid ${tokens.hair}`,
                  }}>
                    <span style={{
                      fontFamily: 'ui-monospace, SF Mono, Menlo, monospace',
                      fontSize: 12.5, fontWeight: 600, color: tokens.text2,
                    }}>{p}</span>
                    <span style={{ fontSize: 11.5, color: tokens.text3 }}>0 views</span>
                  </div>
                )) : (
                  <div style={{ fontSize: 13, color: tokens.okTx, fontWeight: 600 }}>
                    Every module has been opened.
                  </div>
                )}
              </Card>
            </div>

            {/* journeys + friction */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 18, marginTop: 18 }}>
              <Card title="Journeys" hint={snapshot.pageDataAvailable ? 'where they go next' : 'not tracked'}>
                {snapshot.journeys?.length ? snapshot.journeys.map((j, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '11px 0',
                    borderTop: i ? `1px solid ${tokens.hair}` : 'none',
                  }}>
                    <span style={{
                      padding: '4px 10px', borderRadius: 9, background: tokens.panel2,
                      border: `1px solid ${tokens.border2}`, fontSize: 12, fontWeight: 600,
                      fontFamily: 'ui-monospace, SF Mono, Menlo, monospace', color: tokens.text2,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150,
                    }}>{j.f}</span>
                    <span style={{ color: tokens.text3 }}>→</span>
                    <span style={{
                      padding: '4px 10px', borderRadius: 9, background: tokens.accentWeak,
                      fontSize: 12, fontWeight: 600, color: tokens.accentTx,
                      fontFamily: 'ui-monospace, SF Mono, Menlo, monospace',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150,
                    }}>{j.t}</span>
                    <span style={{
                      marginLeft: 'auto', fontWeight: 700, fontSize: 13, color: tokens.text2,
                      fontVariantNumeric: 'tabular-nums',
                    }}>{fmt(j.n)}</span>
                  </div>
                )) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<span style={{ color: tokens.text2 }}>
                      {snapshot.pageDataAvailable ? 'No navigation data yet.' : 'Needs page-level tracking.'}
                    </span>} />
                )}
              </Card>

              <Card title="Friction" hint={snapshot.pageDataAvailable ? 'rage clicks' : 'not tracked'}>
                {snapshot.rageclicks?.length ? snapshot.rageclicks.slice(0, 8).map((r) => (
                  <BarRow key={r.p} label={r.p} value={r.n} max={rageMax} tone="bad" width={150} />
                )) : (
                  <div style={{ fontSize: 13, color: snapshot.pageDataAvailable ? tokens.okTx : tokens.text2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 }}>
                    <MousePointerClick size={14} />
                    {snapshot.pageDataAvailable ? 'No rage clicks recorded.' : 'Needs page-level tracking.'}
                  </div>
                )}
              </Card>
            </div>

            {/* delivered */}
            <div style={{ marginTop: 18 }}>
              <Card title="Delivered to them" hint="reports and emails we sent" pad={false}>
                {delivered.length === 0 ? (
                  <div style={{ padding: '8px 16px 20px' }}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={<span style={{ color: tokens.text2 }}>Nothing sent to this org yet.</span>} />
                  </div>
                ) : (
                  <Table
                    rowKey={(r) => String(r._id)}
                    size="small"
                    pagination={delivered.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
                    scroll={{ x: 'max-content' }}
                    columns={[
                      {
                        title: 'Report', key: 'w',
                        render: (_, r) => (
                          <div>
                            <div style={{ fontWeight: 600, color: tokens.text }}>{r.windowLabel || 'Weekly report'}</div>
                            <div style={{ fontSize: 11.5, color: tokens.text3 }}>
                              {r.stats?.peopleActive ? `${r.stats.peopleActive} people active` : ''}
                            </div>
                          </div>
                        ),
                      },
                      {
                        title: 'Recipients', dataIndex: 'recipients', key: 'recipients',
                        render: (v) => (
                          <span style={{
                            fontFamily: 'ui-monospace, SF Mono, Menlo, monospace',
                            fontSize: 12, color: tokens.text2,
                          }}>{v?.length ? v.join(', ') : '—'}</span>
                        ),
                      },
                      {
                        title: 'Sent', key: 'sentAt',
                        render: (_, r) => (
                          <span style={{ color: tokens.text2 }}>
                            {r.sentAt ? dayjs(r.sentAt).format('D MMM, HH:mm') : dayjs(r.createdAt).format('D MMM')}
                          </span>
                        ),
                      },
                      {
                        title: 'Status', dataIndex: 'status', key: 'status', align: 'right',
                        render: (v) => {
                          const tone = v === 'sent' ? 'ok' : v === 'failed' ? 'bad' : 'off';
                          return <Tag style={{
                            background: toneBg(tone), color: toneColor(tone), border: 'none',
                            borderRadius: 8, fontWeight: 600, fontSize: 11.5,
                          }}>{v}</Tag>;
                        },
                      },
                    ]}
                    dataSource={delivered}
                  />
                )}
              </Card>
            </div>

            {/* raw events */}
            <div style={{ marginTop: 18 }}>
              <Card title="Recent activity" hint="raw events · retained 7 days">
                {events.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<span style={{ color: tokens.text2 }}>No events in the last 7 days.</span>} />
                ) : events.slice(0, 40).map((e, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 11, padding: '9px 0', fontSize: 12.5,
                    borderTop: i ? `1px solid ${tokens.hair}` : 'none',
                  }}>
                    <span style={{
                      color: tokens.text3, fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap', minWidth: 104, fontSize: 11.5,
                    }}>{dayjs(e.at).format('D MMM HH:mm')}</span>
                    <span style={{ color: tokens.text, fontWeight: 600, minWidth: 130 }}>{e.name}</span>
                    <span style={{ color: tokens.text2 }}>
                      {e.action.replace(/_/g, ' ')}
                      {e.meta?.src ? <span style={{ color: tokens.text3 }}> · {e.meta.src}</span> : null}
                    </span>
                  </div>
                ))}
              </Card>
            </div>
          </>
        )}

        {/* per-user drawer */}
        <Drawer
          open={!!drawerUser}
          onClose={() => { setDrawerUser(null); setDrawerData(null); }}
          width={520}
          title={
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: tokens.text }}>{drawerUser?.name}</div>
              <div style={{ fontSize: 11.5, color: tokens.text3, marginTop: 2 }}>{drawerUser?.email}</div>
            </div>
          }
        >
          {drawerLoading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : !drawerData ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span style={{ color: tokens.text2 }}>No rollup data for this person yet.</span>} />
          ) : (
            <>
              {drawerUser?.label ? (
                <div style={{ marginBottom: 16 }}>
                  <Tag style={{
                    background: toneBg(LABEL_TONE[drawerUser.label] || 'off'),
                    color: toneColor(LABEL_TONE[drawerUser.label] || 'off'),
                    border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 12, padding: '4px 10px',
                  }}>{drawerUser.label}</Tag>
                </div>
              ) : null}

              {[
                ['Logins', fmt(drawerData.logins)],
                ['Days active', `${drawerData.daysActive} / ${drawerData.windowDays}`],
                ['Best streak', `${drawerData.bestStreak} days`],
                ['Longest gap', drawerData.maxGapDays ? `${drawerData.maxGapDays} days` : '—'],
                ['Last seen', sinceHuman(drawerData.lastAt)],
              ].map(([k, val]) => (
                <div key={k} style={{
                  display: 'flex', justifyContent: 'space-between', gap: 18,
                  padding: '9px 0', borderBottom: `1px solid ${tokens.hair}`, fontSize: 13,
                }}>
                  <span style={{ color: tokens.text3, fontWeight: 600 }}>{k}</span>
                  <span style={{ fontWeight: 600, color: tokens.text, fontVariantNumeric: 'tabular-nums' }}>{val}</span>
                </div>
              ))}

              {Object.keys(drawerData.actions || {}).length ? (
                <div style={{ marginTop: 22 }}>
                  <div style={{
                    fontSize: 11.5, fontWeight: 700, color: tokens.text3,
                    textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10,
                  }}>What they did</div>
                  {Object.entries(drawerData.actions).map(([k, n]) => (
                    <div key={k} style={{
                      display: 'flex', justifyContent: 'space-between',
                      padding: '8px 0', fontSize: 13, borderTop: `1px solid ${tokens.hair}`,
                    }}>
                      <span style={{ color: tokens.text2 }}>{k.replace(/_/g, ' ')}</span>
                      <span style={{ fontWeight: 600, color: tokens.text }}>{fmt(n)}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              {drawerData.series?.length ? (
                <div style={{ marginTop: 22 }}>
                  <div style={{
                    fontSize: 11.5, fontWeight: 700, color: tokens.text3,
                    textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10,
                  }}>Daily logins</div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 60 }}>
                    {drawerData.series.map((s, i) => {
                      const max = Math.max(...drawerData.series.map((x) => x.logins), 1);
                      return (
                        <AntTooltip key={i} title={`${dayjs(s.day).format('D MMM')}: ${s.logins}`}>
                          <i style={{
                            flex: 1, minWidth: 3,
                            height: `${Math.max(3, (s.logins / max) * 100)}%`,
                            background: s.logins ? tokens.accent : tokens.hair,
                            borderRadius: 2, display: 'block',
                          }} />
                        </AntTooltip>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </Drawer>
      </>
    );
  };

  return (
    <>
      <style>{designSystem}</style>
      <AdminThemeProvider>
        <div className={`${ADMIN_ROOT} h-screen flex overflow-hidden`}>
          <div className="tg-shell">
            <AdminSidebar activeKey="org-pattern" />
            <div className="flex flex-col w-0 flex-1 overflow-hidden">
              <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
                <div className="py-6 px-5 sm:px-6 w-full" style={{ maxWidth: 1360, margin: '0 auto' }}>
                  {orgId ? renderOrg() : renderLeague()}
                </div>
              </main>
            </div>
          </div>
        </div>
      </AdminThemeProvider>
    </>
  );
};

export default OrgPattern;
